import { FC, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import TableGridItem from '../../UI/TableGridItem';
import { createColumnDefs, defaultColumnDefs, WorkingHour } from '../helpers/utils';
import { getFooterRowStyle, percentageCellStyleRenderer } from '../../../utils/agGridUtils';
import { createWorkingHoursRowFilter } from '../../../utils/workingHoursUtils';
import type { Dayjs } from '@utils/dayjs';

interface DayTimeSlot {
    date: string;
    isWorking?: boolean;
    total?: number;
}

interface TotalsTabPanelProps {
    allDatesInAMonth: Dayjs[];
    rowData: { [k: string]: string | DayTimeSlot }[];
    handleCellValueChange: () => void;
    hasCompletedCalculations: boolean;
    isLoading?: boolean;
    hideNonWorkingTime?: boolean;
    workingHours?: WorkingHour[];
}

const TotalsTabPanel: FC<TotalsTabPanelProps> = ({
    allDatesInAMonth,
    rowData,
    handleCellValueChange,
    hasCompletedCalculations,
    isLoading,
    hideNonWorkingTime = true,
    workingHours,
}) => {
    const { t } = useTranslation();
    const gridRef = useRef(null);

    // Filter rows to hide non-working time slots if toggle is enabled
    const filteredRowData = useMemo(() => {
        const filterFn = createWorkingHoursRowFilter(allDatesInAMonth, workingHours, hideNonWorkingTime);
        return rowData.filter(filterFn);
    }, [rowData, hideNonWorkingTime, allDatesInAMonth, workingHours]);

    const maxTotalValue = useMemo(() => {
        return Math.max(
            ...rowData.map((row) =>
                Math.max(...Object.values(row).map((v) => (typeof v === 'string' ? 0 : v.total || 0)))
            )
        );
    }, [rowData]);

    // Memoize cell style renderer for performance
    const cellStyleRenderer = useCallback(
        ({ value, node }: any) =>
            !node.footer
                ? percentageCellStyleRenderer({
                      value: value,
                      maxValue: maxTotalValue ?? 0,
                  })
                : undefined,
        [maxTotalValue]
    );

    // Memoize column definitions to avoid re-creation on every render
    const columnDefs = useMemo(
        () =>
            createColumnDefs({
                allDatesInAMonth,
                valuePropertyName: 'total',
                hasFooter: true,
                footerRowText: t('common.sum'),
                alwaysAllowEdit: true,
                editable: !hasCompletedCalculations,
                cellStyleRenderer,
            }),
        [allDatesInAMonth, hasCompletedCalculations, cellStyleRenderer, t]
    );

    return (
        <TableGridItem
            ref={gridRef}
            rowData={filteredRowData}
            defaultColDef={{ ...defaultColumnDefs, editable: true }}
            columnDefs={columnDefs}
            columnHoverHighlight={true}
            grandTotalRow={'bottom'}
            getRowStyle={getFooterRowStyle}
            onCellValueChanged={handleCellValueChange}
            loading={isLoading}
            cellSelection={true}
            suppressScrollOnNewData={true}
        />
    );
};

export default TotalsTabPanel;
